import { useMemo, useState } from 'react';
import { AsyncBoundary, Card, KpiCard, Notice } from '@kollez/design-system';
import { formatINR, formatIndianDate } from '@kollez/shared';
import { useAuthStore } from '@/lib/authStore';
import { usePartnerEarnings, usePartnerTransactions, type PartnerTransactionRow } from '@/lib/queries';

const STATUS_TONE: Record<PartnerTransactionRow['status'], string> = {
  pending: 'badge-warning',
  succeeded: 'badge-success',
  failed: 'badge-danger',
  refunded: 'badge',
};

/**
 * /company/earnings (v1.6 §10) — public-catalog earnings rollup for the recruiter's own
 * partner org. Kollez collects money on paid workflow stages and splits it four ways; this
 * page shows the partner's slice.
 *
 * - Lifetime KPIs: total, succeeded, pending, txn count.
 * - 6-month bars for succeeded + pending amounts (stacked visually via two rows).
 * - When the partner has no earnings yet, empty-state hint links to the openings page.
 */
export function CompanyEarningsPage() {
  const user = useAuthStore((s) => s.user);
  const partnerId = user?.partner ?? null;
  const q = usePartnerEarnings(partnerId);
  const [status, setStatus] = useState<'' | PartnerTransactionRow['status']>('');
  const tx = usePartnerTransactions(partnerId, { status: status || undefined, limit: 50 });

  const maxMonthly = useMemo(() => {
    const rows = q.data?.byMonth ?? [];
    if (rows.length === 0) return 1;
    return Math.max(
      1,
      ...rows.map((r) => r.succeededInr + r.pendingInr),
    );
  }, [q.data]);

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Kollez Public · Revenue</p>
          <h1 className="h-page">Earnings</h1>
          <p className="subhead">Your slice of paid Kollez Public workflow stages. Real gateway settles later; pending rows are already committed.</p>
        </div>
      </div>

      {!partnerId ? (
        <Notice tone="warning">
          <b>No partner scope.</b> This page needs a partner_admin/recruiter session. Sign in as your partner org account.
        </Notice>
      ) : null}

      <AsyncBoundary
        kind="kpis"
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && (q.data?.lifetime.txnCount ?? 0) === 0}
        emptyTitle="No earnings yet"
        emptyHint="Post a workflow with a paid stage on the Kollez Public catalog and revenue lands here as students transition through it."
      >
        <div className="grid gap-3 md:grid-cols-4">
          <KpiCard label="Lifetime" value={formatINR(q.data?.lifetime.totalInr ?? 0)} />
          <KpiCard label="Succeeded" value={formatINR(q.data?.lifetime.succeededInr ?? 0)} />
          <KpiCard label="Pending" value={formatINR(q.data?.lifetime.pendingInr ?? 0)} />
          <KpiCard label="Transactions" value={q.data?.lifetime.txnCount ?? 0} />
        </div>

        <Card padding="lg">
          <h2 className="h-section mb-3">Last 6 months</h2>
          <div className="grid gap-2">
            {(q.data?.byMonth ?? []).map((r) => {
              const total = r.succeededInr + r.pendingInr;
              const pct = Math.round((total / maxMonthly) * 100);
              const succeededPct = total === 0 ? 0 : Math.round((r.succeededInr / total) * 100);
              return (
                <div key={r.month} className="flex items-center gap-3">
                  <div style={{ width: 74, fontSize: 13 }}>{r.month}</div>
                  <div
                    aria-label={`${r.month}: ${formatINR(total)} total`}
                    style={{
                      flex: 1,
                      height: 22,
                      background: 'rgb(var(--surface-subtle))',
                      borderRadius: 4,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${pct}%`,
                        height: '100%',
                        display: 'flex',
                      }}
                    >
                      <div style={{ width: `${succeededPct}%`, background: 'rgb(var(--accent-primary))' }} />
                      <div style={{ flex: 1, background: 'rgb(var(--accent-warning))' }} />
                    </div>
                  </div>
                  <div style={{ width: 120, textAlign: 'right', fontSize: 13 }}>{formatINR(total)}</div>
                </div>
              );
            })}
          </div>
          <p className="cell-sub mt-3">
            <span style={{ display: 'inline-block', width: 10, height: 10, background: 'rgb(var(--accent-primary))', borderRadius: 2, marginRight: 6 }} />
            Succeeded
            <span style={{ display: 'inline-block', width: 10, height: 10, background: 'rgb(var(--accent-warning))', borderRadius: 2, margin: '0 6px 0 14px' }} />
            Pending
          </p>
        </Card>
      </AsyncBoundary>

      {partnerId && (
        <Card padding="none">
          <div className="card-head flex items-center justify-between">
            <h2 className="h-section">Transactions</h2>
            <select
              className="input"
              value={status}
              onChange={(e) => setStatus(e.target.value as '' | PartnerTransactionRow['status'])}
              style={{ maxWidth: 200 }}
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="succeeded">Succeeded</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
          <AsyncBoundary
            kind="table"
            inCard
            loading={tx.isLoading}
            error={tx.error}
            empty={!tx.isLoading && (tx.data?.length ?? 0) === 0}
            emptyTitle="No transactions"
            emptyHint="Kollez Public catalog stages that charge students post here per settlement."
            onRetry={() => void tx.refetch()}
          >
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Stage</th>
                    <th className="hide-mobile">Method</th>
                    <th className="r">Amount</th>
                    <th className="r hide-mobile">Partner share</th>
                    <th>Status</th>
                    <th className="hide-mobile">Initiated</th>
                  </tr>
                </thead>
                <tbody>
                  {(tx.data ?? []).map((t) => (
                    <tr key={t._id}>
                      <td>
                        <div className="cell-title">{t.stageKey ?? '—'}</div>
                        <div className="cell-sub">
                          {t.monetizationModel ?? 'flat'}{t.chargeOn ? ` · ${t.chargeOn}` : ''}
                        </div>
                      </td>
                      <td className="hide-mobile cell-sub">{t.paymentMethod ?? '—'}</td>
                      <td className="r"><b>{formatINR(t.amountInr)}</b></td>
                      <td className="r hide-mobile cell-sub">
                        {t.split?.partnerInr !== undefined ? formatINR(t.split.partnerInr) : '—'}
                      </td>
                      <td>
                        <span className={`badge ${STATUS_TONE[t.status]}`}>{t.status}</span>
                      </td>
                      <td className="hide-mobile cell-sub">
                        {t.initiatedAt ? formatIndianDate(new Date(t.initiatedAt)) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AsyncBoundary>
        </Card>
      )}
    </div>
  );
}
